import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { loadUser } from "@/backend/http/user";
import { setLanguage } from "@/language";
import type { Board } from "@/model/board";
import { isPlanningBoard } from "@/model/board";
import type { Tenant } from "@/model/session";
import type { AuthUser, Role } from "@/model/user";
import { baseUser, technicalUser } from "@/model/user";
import { useGlobalI18n } from "@/translations/i18n";
import { asReadonly } from "@/utils/vue";

import { useActivityStore } from "./activity";
import { usePanModeStore } from "./panMode";

const { t } = useGlobalI18n();

export function noAssignee(color: string): AuthUser {
  return noUser(color, /*$t*/ "general.noAssignee");
}

export function noReporter(color: string): AuthUser {
  return noUser(color, /*$t*/ "general.noReporter");
}

export function unassignedUser(color: string): AuthUser {
  return noUser(color, /*$t*/ "general.unassigned");
}

export function isNoUser(user: AuthUser): boolean {
  return user.type === "no";
}

function noUser(color: string, nameKey: string): AuthUser {
  return { ...baseUser, type: "no", id: "no", name: t(nameKey), color };
}

export const useUserStore = defineStore("user", () => {
  const loginData = ref({ email: "", tenants: [] as Tenant[] });
  const techUser = ref(technicalUser("", "", "", "v1"));
  const currentUser = ref<AuthUser | null>(null);

  function isAllowed(permission: Permission) {
    return !hasNoPermissions.value && permissions[techUser.value.role].includes(permission);
  }

  // during pan mode and board history visualization, the board is frozen. The user is not allowed to do anything
  const hasNoPermissions = computed(
    () =>
      useActivityStore().tab === "visualize" ||
      usePanModeStore().active ||
      techUser.value.role === "observer",
  );

  async function loadCurrentUser() {
    const user = await loadUser(
      { id: techUser.value.id, caller: "loadCurrentUser" },
      { useCache: false },
    );
    currentUser.value = user;
    await setLanguage(user.preferredLanguage);
    return user;
  }

  return {
    loginData: asReadonly(loginData),
    technicalUser: techUser,
    currentUser: asReadonly(currentUser),

    isTraining: computed(() => techUser.value.company.startsWith("pips-")),
    hasNoPermissions,
    isAllowed,
    isNonTeamZoomAllowed: (board: Board) =>
      (!isPlanningBoard(board.type) && board.type !== "risk") || isAllowed("nonTeamZoom"),

    setLoginData: (email: string, tenants: Tenant[]) => void (loginData.value = { email, tenants }),
    resetUser: () => (techUser.value = technicalUser("", "", "", "v1")),
    loadCurrentUser,
  };
});

type Permission =
  | "edit"
  | "nonTeamZoom"
  | "deleteBoard"
  | "cockpit"
  | "useWithoutLicense"
  | "editBoard"
  | "duplicateFlex"
  | "manageConfidenceVote"
  | "contactSupport"
  | "configureAlm"
  | "sightline";

const permissions: Record<Role, Permission[]> = {
  observer: [],
  user: ["edit", "editBoard"],
  planning_interval_admin: [
    "edit",
    "nonTeamZoom",
    "deleteBoard",
    "cockpit",
    "editBoard",
    "duplicateFlex",
    "manageConfidenceVote",
    "sightline",
  ],
  admin: [
    "edit",
    "nonTeamZoom",
    "deleteBoard",
    "cockpit",
    "useWithoutLicense",
    "editBoard",
    "duplicateFlex",
    "manageConfidenceVote",
    "contactSupport",
    "configureAlm",
    "sightline",
  ],
};
